// src/pages/SocialDashboardPage.js
//
// Top-level overview page for the Social Media Content Management feature.
// Lives at /social/dashboard (routing wired elsewhere).
//
// Status -> color mapping used across the Social Media Content Management
// feature (Dashboard / Calendar / Approvals / Reports). Kept here as the
// canonical reference comment; ContentCalendarPage.js and
// SocialReportsPage.js reuse the same hex values for visual consistency.
//
//   New Request               #9ca3af  (neutral gray - not started)
//   Content Writing           #60a5fa  (blue - writer working)
//   Content Ready             #3b82f6  (deeper blue - writing done)
//   Design / Editing          #a78bfa  (purple - designer working)
//   Design Ready              #8b5cf6  (deeper purple - design done)
//   Internal Review           #f59e0b  (amber - needs internal reviewer)
//   Changes Requested         #f97316  (orange - sent back for rework)
//   Ready for Client Approval #fbbf24  (light amber - needs to be sent out)
//   Client Approval           #eab308  (gold - waiting on client)
//   Approved                  #14b8a6  (teal - client said yes)
//   Scheduled                 #6366f1  (indigo - queued to publish)
//   Published                 #22c55e  (green - done/live)
//   Cancelled                 #6b7280  (dark gray - dead)

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useSocialLanguage } from '../hooks/useSocialLanguage';
import LanguageToggle from '../components/LanguageToggle';
import { getStatusLabel, getPlatformLabel } from '../utils/socialEnumLabels';
import styles from '../styles/SocialDashboardPage.module.css';

const TRANSLATIONS = {
  en: {
    noAccess: 'You do not have access to this page.',
    pageTitle: 'Social Media Content Dashboard',
    loading: 'Loading dashboard...',
    activeContentTasks: 'Active Content Tasks',
    publishedThisMonth: 'Published This Month',
    scheduledUpcoming: 'Scheduled (Upcoming)',
    awaitingClientApproval: 'Awaiting Client Approval',
    overdue: 'Overdue',
    internalReviewQueue: 'Internal Review Queue',
    quickLinks: 'Quick Links',
    contentCalendar: 'Content Calendar',
    approvalsQueue: 'Approvals Queue',
    reports: 'Reports',
    allContentTasks: 'All Content Tasks',
    newContentTask: '+ New Content Task',
    clientSocialPages: 'Client Social Pages',
    upcomingDueDates: 'Upcoming Due Dates',
    noUpcomingTasks: 'No upcoming content tasks.',
    untitled: 'Untitled',
    noClient: 'No client',
    due: 'Due',
  },
  ar: {
    noAccess: 'لا تملك صلاحية الوصول إلى هذه الصفحة.',
    pageTitle: 'لوحة محتوى وسائل التواصل الاجتماعي',
    loading: 'جارٍ تحميل لوحة التحكم...',
    activeContentTasks: 'مهام المحتوى النشطة',
    publishedThisMonth: 'تم نشره هذا الشهر',
    scheduledUpcoming: 'مجدول (قريباً)',
    awaitingClientApproval: 'بانتظار موافقة العميل',
    overdue: 'متأخر',
    internalReviewQueue: 'قائمة المراجعة الداخلية',
    quickLinks: 'روابط سريعة',
    contentCalendar: 'تقويم المحتوى',
    approvalsQueue: 'قائمة الموافقات',
    reports: 'التقارير',
    allContentTasks: 'جميع مهام المحتوى',
    newContentTask: '+ مهمة محتوى جديدة',
    clientSocialPages: 'صفحات العميل على وسائل التواصل',
    upcomingDueDates: 'تواريخ الاستحقاق القادمة',
    noUpcomingTasks: 'لا توجد مهام محتوى قادمة.',
    untitled: 'بدون عنوان',
    noClient: 'بدون عميل',
    due: 'الاستحقاق',
  },
};

const STATUS_COLORS = {
  'New Request': '#9ca3af',
  'Content Writing': '#60a5fa',
  'Content Ready': '#3b82f6',
  'Design / Editing': '#a78bfa',
  'Design Ready': '#8b5cf6',
  'Internal Review': '#f59e0b',
  'Changes Requested': '#f97316',
  'Ready for Client Approval': '#fbbf24',
  'Client Approval': '#eab308',
  Approved: '#14b8a6',
  Scheduled: '#6366f1',
  Published: '#22c55e',
  Cancelled: '#6b7280',
};

const NON_ACTIVE_STATUSES = ['Published', 'Cancelled'];

// Mirrors the "Overdue" definition from the feature spec:
// status NOT in [Published, Cancelled] AND dueDate (via .toDate()) is before now.
const isOverdue = (task) => {
  if (NON_ACTIVE_STATUSES.includes(task.status)) return false;
  if (!task.dueDate) return false;
  return task.dueDate.getTime() < Date.now();
};

const parseTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return null;
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const allowedRoles = ['Manager', 'Accounts Manager', 'Sales'];

const SocialDashboardPage = () => {
  const { userRole } = useAuth();
  const { language, toggleLanguage, isRTL } = useSocialLanguage();
  const t = TRANSLATIONS[language];
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    const tasksCol = collection(db, 'contentTasks');
    const unsubscribe = onSnapshot(
      tasksCol,
      (snapshot) => {
        const fetched = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            dueDate: parseTimestamp(data.dueDate),
            scheduledDate: parseTimestamp(data.scheduledDate),
            publishedDate: parseTimestamp(data.publishedDate),
          };
        });
        setTasks(fetched);
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to contentTasks:', err);
        setError('Failed to load content tasks.');
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const stats = useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    let active = 0;
    let publishedThisMonth = 0;
    let scheduledUpcoming = 0;
    let awaitingClient = 0;
    let overdue = 0;
    let internalReviewQueue = 0;

    tasks.forEach((task) => {
      const statusActive = !NON_ACTIVE_STATUSES.includes(task.status);
      if (statusActive) active++;

      if (task.status === 'Published' && task.publishedDate) {
        if (task.publishedDate >= startOfMonth && task.publishedDate < startOfNextMonth) {
          publishedThisMonth++;
        }
      }

      if (task.status === 'Scheduled') scheduledUpcoming++;

      if (task.status === 'Client Approval') awaitingClient++;

      if (isOverdue(task)) overdue++;

      if (task.status === 'Internal Review' || task.status === 'Ready for Client Approval') {
        internalReviewQueue++;
      }
    });

    return {
      active,
      publishedThisMonth,
      scheduledUpcoming,
      awaitingClient,
      overdue,
      internalReviewQueue,
    };
  }, [tasks]);

  const upcomingTasks = useMemo(() => {
    return tasks
      .filter((task) => !NON_ACTIVE_STATUSES.includes(task.status) && task.dueDate)
      .sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime())
      .slice(0, 8);
  }, [tasks]);

  const hasAccess = !userRole || allowedRoles.includes(userRole);

  if (!hasAccess) {
    return (
      <div className={styles.pageContainer} dir={isRTL ? 'rtl' : 'ltr'}>
        <p className={styles.errorMessage}>{t.noAccess}</p>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={styles.header}>
        <h2 className={styles.pageTitle}>{t.pageTitle}</h2>
        <LanguageToggle language={language} onToggle={toggleLanguage} />
      </div>

      {error && <p className={styles.errorMessage}>{error}</p>}

      {loading ? (
        <div className={styles.loadingIndicator}>{t.loading}</div>
      ) : (
        <>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <h4>{t.activeContentTasks}</h4>
              <p className={styles.summaryValue}>{stats.active}</p>
            </div>
            <div className={styles.summaryCard}>
              <h4>{t.publishedThisMonth}</h4>
              <p className={styles.summaryValue}>{stats.publishedThisMonth}</p>
            </div>
            <div className={styles.summaryCard}>
              <h4>{t.scheduledUpcoming}</h4>
              <p className={styles.summaryValue}>{stats.scheduledUpcoming}</p>
            </div>
            <div className={styles.summaryCard}>
              <h4>{t.awaitingClientApproval}</h4>
              <p className={styles.summaryValue}>{stats.awaitingClient}</p>
            </div>
            <div className={`${styles.summaryCard} ${stats.overdue > 0 ? styles.dangerCard : ''}`}>
              <h4>{t.overdue}</h4>
              <p className={`${styles.summaryValue} ${stats.overdue > 0 ? styles.dangerValue : ''}`}>
                {stats.overdue}
              </p>
            </div>
            <div className={styles.summaryCard}>
              <h4>{t.internalReviewQueue}</h4>
              <p className={styles.summaryValue}>{stats.internalReviewQueue}</p>
            </div>
          </div>

          <div className={styles.quickLinksSection}>
            <h3 className={styles.sectionTitle}>{t.quickLinks}</h3>
            <div className={styles.quickLinksGrid}>
              <Link to="/social/calendar" className={styles.quickLinkCard}>
                {t.contentCalendar}
              </Link>
              <Link to="/social/approvals" className={styles.quickLinkCard}>
                {t.approvalsQueue}
              </Link>
              <Link to="/social/reports" className={styles.quickLinkCard}>
                {t.reports}
              </Link>
              <Link to="/social/tasks" className={styles.quickLinkCard}>
                {t.allContentTasks}
              </Link>
              <Link to="/social/tasks/new" className={styles.quickLinkCard}>
                {t.newContentTask}
              </Link>
              <Link to="/social/pages" className={styles.quickLinkCard}>
                {t.clientSocialPages}
              </Link>
            </div>
          </div>

          <div className={styles.upcomingSection}>
            <h3 className={styles.sectionTitle}>{t.upcomingDueDates}</h3>
            {upcomingTasks.length === 0 ? (
              <p className={styles.noDataMessage}>{t.noUpcomingTasks}</p>
            ) : (
              <ul className={styles.upcomingList}>
                {upcomingTasks.map((task) => (
                  <li key={task.id} className={styles.upcomingItem}>
                    <Link to={`/social/tasks/${task.id}`} className={styles.upcomingLink}>
                      <span
                        className={styles.statusDot}
                        style={{ backgroundColor: STATUS_COLORS[task.status] || '#9ca3af' }}
                        title={getStatusLabel(task.status, language)}
                      />
                      <span className={styles.upcomingTitle}>{task.title || t.untitled}</span>
                      <span className={styles.upcomingClient}>{task.clientName || t.noClient}</span>
                      <span className={styles.upcomingPlatform}>{getPlatformLabel(task.platform, language)}</span>
                      <span className={isOverdue(task) ? styles.overdueDate : styles.upcomingDue}>
                        {t.due} {formatDate(task.dueDate)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SocialDashboardPage;
